import * as React from "react";
import {IPerformanceMetrics, IPortfolio, IPortfolioMetrics} from "../../api/PortfolioApi";
import {useState} from "react";
import {formatPercentBasedOnLocale, roundNumber} from "../../utils/numberUtil";
import {ModelPortfolioSlider} from "./ModelPortfolioSlider";
import {ITooltipProps, Tooltip} from "../common/Tooltip";

export interface IModelPerformanceMetricsProps {
    selectedModelPortfolio: IPortfolio;
    performanceMetrics: IPerformanceMetrics;
}

export const METRIC_EXPECTED_NOMINAL_RETURN = "10-Year Expected Nominal Return";
export const METRIC_SUB_NAME_EXPECTED_NOMINAL_RETURN_GROSS = "Net of Investment Management Fees,  Gross of Investment Advisory Fees";
export const METRIC_SUB_NAME_EXPECTED_NOMINAL_RETURN_FEE = "Net of Investment Management Fees and Investment Advisory Fees";
export const METRIC_INVESTMENT_ADVISORY_FEES = "Investment Advisory Fees";
export const METRIC_VOLATILITY = "10-Year Volatility";
export const METRIC_RISK_REWARD_RATIO = "Risk/Reward (Sharpe) Ratio";
export const METRIC_PORTFOLIO_RISK = "1 year Portfolio Value at Risk (5%)";
export const METRIC_PORTFOLIO_UPSIDE = "1 year Portfolio Upside (5%)";

export const TOOLTIP_EXPECTED_NOMINAL_RETURN_GROSS = "Expected Nominal Return is a forward-looking projection and estimate which is hypothetical in nature and does not reflect the performance of any actual investment portfolio or results that were achieved by a client portfolio under management or advisement.  Expected Nominal Return reflects the deduction of fees charged by external investment managers which may be in the form of a management fee and/or fees and expenses applicable to pooled investment vehicles but does not reflect the deduction of Aon’s investment advisory fees.";
export const TOOLTIP_INVESTMENT_ADVISORY_FEES1 = "Model fee for illustrative purposes only. Aon’s investment advisory fees are negotiated in advance, on a per-client basis and will vary depending on several factors, including, but not limited to the nature and complexity of the client’s circumstances, the services to be provided, and other factors. In general, traditional advisory-only consulting fees (non-discretionary investment advice) are typically billed as a fixed fee or retainer, encompassing all the services provided by Aon while delegated or Outsourced Chief Investment Officer (OCIO) fees (discretionary investment management) are typically calculated as a percentage of assets under management (which in certain cases is subject to a minimum annual fee).";
export const TOOLTIP_INVESTMENT_ADVISORY_FEES2 = "While in certain arrangements, Aon’s investment advisory fees may not directly reduce an investment portfolio’s return, this “model fee” is intended to help demonstrate the effect that fees and expenses will have on future performance. You can adjust this value to see how it may potentially impact your projected return and risk metrics.";
export const TOOLTIP_EXPECTED_NOMINAL_RETURN = "Expected Nominal Return is a forward-looking projection and estimate which is hypothetical in nature and does not reflect the performance of any actual investment portfolio or results that were achieved by a client portfolio under management or advisement.  Expected Nominal Return reflects the deduction of fees charged by external investment managers which may be in the form of a management fee and/or fees and expenses applicable to pooled investment vehicles and the deduction of a model fee, equal to the highest asset-based fee that would be charged for Aon’s discretionary investment management services. As noted, depending on the services provided and fee negotiated with the client, Aon’s investment advisory fees may not directly reduce an investment portfolio’s return. This “model fee” is intended to help demonstrate the effect that fees and expenses will have on future performance and you can adjust this value to see how it may potentially impact your projected return and risk metrics.";
export const TOOLTIP_VOLATILITY = "A measure of how much the portfolio’s returns are expected to fluctuate over the next 10 years. Higher volatility means greater uncertainty and potential risk.";
export const TOOLTIP_RISK_REWARD_RATIO = "A metric that compares the portfolio’s return to its risk. A higher Sharpe Ratio indicates better risk-adjusted performance, helping evaluate whether the return justifies the risk taken.";
export const TOOLTIP_PORTFOLIO_RISK = "There is a 5% chance that the portfolio will experience a loss equal to or greater than this amount over the next year. This metric estimates the worst-case scenario under normal market conditions, helping to understand potential downside risk.";
export const TOOLTIP_PORTFOLIO_UPSIDE = "There is a 5% chance that the portfolio’s return over the next year will meet or exceed this value. It represents a best-case scenario under normal market conditions, helping to understand the potential for significant upside.";

export const ModelPerformanceMetrics: React.FunctionComponent<IModelPerformanceMetricsProps> = (props) => {
    interface IRowProps {
        label: string;
        modelValue: number | string;
        currentPlanValue: number | string;
        tooltipText: string[];
        subTile?: string;
    }
    const [investmentAdvisoryFees, setInvestmentAdvisoryFees] = useState<number>(0.50);
    const hasNominalReturn =
        (selectedModelPortfolioMetrics: IPortfolioMetrics, currentPlanPortfolioMetrics: IPortfolioMetrics) => {
        return selectedModelPortfolioMetrics.portfolioMedianLogNormReturn
            && currentPlanPortfolioMetrics.portfolioMedianLogNormReturn;
    };

    const renderTableHeaderTooltip = (tooltipTitle: string, tooltipText: string[], tooltipSubtitle?: string) => {
        const tooltipProps: ITooltipProps = {
            iconClass: "fal fa-info-circle clickable model-portfolio-metrics__tooltip",
            hoverText: tooltipText,
            hoverSubtitle: tooltipSubtitle,
            hoverTitle: tooltipTitle
        };

        return <Tooltip {...tooltipProps}/>;
    };

    const renderRowSubtitle = (subTile: string | undefined) => {
        return subTile && <p data-testid="model-performance-metrics__metric-sub-name">{subTile}</p>;
    };

    const renderRow = (rowProps: IRowProps) => {
        return <tr>
            <td>
                <span className={"model-performance-metrics__metric-name"}
                      data-testid="model-performance-metrics__metric-name">
                    {rowProps.label}
                </span>
                <span data-testid="model-performance-metrics__tooltip">
                    {renderTableHeaderTooltip(rowProps.label, rowProps.tooltipText, rowProps.subTile)}
                </span>
                {renderRowSubtitle(rowProps.subTile)}
            </td>
            <td className={"model-performance-metrics__model-value"}
                data-testid="model-performance-metrics__model-value">
                {rowProps.modelValue}
            </td>
            <td className={"model-performance-metrics__current-plan-value"}
                data-testid="model-performance-metrics__current-plan-value">
                {rowProps.currentPlanValue}
            </td>
        </tr>;
    };

    const renderNominalReturn =
        (selectedModelPortfolioMetrics: IPortfolioMetrics, currentPlanPortfolioMetrics: IPortfolioMetrics) => {
        const rowProps: IRowProps = {
            label: METRIC_EXPECTED_NOMINAL_RETURN,
            modelValue: formatPercentBasedOnLocale(selectedModelPortfolioMetrics.portfolioMedianLogNormReturn! * 100),
            currentPlanValue:
                formatPercentBasedOnLocale(currentPlanPortfolioMetrics.portfolioMedianLogNormReturn! * 100),
            subTile: METRIC_SUB_NAME_EXPECTED_NOMINAL_RETURN_GROSS,
            tooltipText: [TOOLTIP_EXPECTED_NOMINAL_RETURN_GROSS]
        };

        return renderRow(rowProps);
    };

    const renderInvestmentAdvisoryFees = () => {
        return <tr>
            <td className={"model-performance-metrics__metric-name"}>
                {METRIC_INVESTMENT_ADVISORY_FEES}
                {renderTableHeaderTooltip(METRIC_INVESTMENT_ADVISORY_FEES,
                    [TOOLTIP_INVESTMENT_ADVISORY_FEES1, TOOLTIP_INVESTMENT_ADVISORY_FEES2])}
            </td>
            <td colSpan={2}>
                <ModelPortfolioSlider onChange={setInvestmentAdvisoryFees} sliderValue={investmentAdvisoryFees}/>
            </td>
        </tr>;
    };

    const calculateInvestmentNominalReturn = (portfolioMetrics: IPortfolioMetrics): string => {
        return formatPercentBasedOnLocale((portfolioMetrics.portfolioMedianLogNormReturn!
            - investmentAdvisoryFees / 100) * 100);
    };

    const renderInvestmentNominalReturn =
        (selectedModelPortfolioMetrics: IPortfolioMetrics, currentPlanPortfolioMetrics: IPortfolioMetrics) => {
        const rowProps: IRowProps = {
            label: METRIC_EXPECTED_NOMINAL_RETURN,
            modelValue: calculateInvestmentNominalReturn(selectedModelPortfolioMetrics),
            currentPlanValue: calculateInvestmentNominalReturn(currentPlanPortfolioMetrics),
            subTile: METRIC_SUB_NAME_EXPECTED_NOMINAL_RETURN_FEE,
            tooltipText: [TOOLTIP_EXPECTED_NOMINAL_RETURN]
        };

        return renderRow(rowProps);
    };

    const renderVolatility =
        (selectedModelPortfolioMetrics: IPortfolioMetrics, currentPlanPortfolioMetrics: IPortfolioMetrics) => {
        if (!selectedModelPortfolioMetrics.portfolioLogNormVol
            || !currentPlanPortfolioMetrics.portfolioLogNormVol) {
            return null;
        }
        const rowProps: IRowProps = {
            label: METRIC_VOLATILITY,
            modelValue: formatPercentBasedOnLocale(selectedModelPortfolioMetrics.portfolioLogNormVol * 100),
            currentPlanValue: formatPercentBasedOnLocale(currentPlanPortfolioMetrics.portfolioLogNormVol * 100),
            tooltipText: [TOOLTIP_VOLATILITY]
        };

        return renderRow(rowProps);
    };

    const calculateRiskRewardRatio = (portfolioMetrics: IPortfolioMetrics): number => {
        return roundNumber(portfolioMetrics.portfolioSharpe!
            - ((investmentAdvisoryFees / 100) / portfolioMetrics.portfolioLogNormVol!));
    };

    const renderRiskRewardRatio =
        (selectedModelPortfolioMetrics: IPortfolioMetrics, currentPlanPortfolioMetrics: IPortfolioMetrics) => {
        if (!selectedModelPortfolioMetrics.portfolioSharpe
            || !currentPlanPortfolioMetrics.portfolioSharpe
            || !selectedModelPortfolioMetrics.portfolioLogNormVol
            || !currentPlanPortfolioMetrics.portfolioLogNormVol) {
            return null;
        }
        const rowProps: IRowProps = {
            label: METRIC_RISK_REWARD_RATIO,
            modelValue: calculateRiskRewardRatio(selectedModelPortfolioMetrics),
            currentPlanValue: calculateRiskRewardRatio(currentPlanPortfolioMetrics),
            tooltipText: [TOOLTIP_RISK_REWARD_RATIO]
        };

        return renderRow(rowProps);
    };

    const calculatePortfolioRisk = (portfolioMetrics: IPortfolioMetrics): string => {
        return formatPercentBasedOnLocale((portfolioMetrics.fivePctVar1y! - investmentAdvisoryFees / 100) * 100);
    };

    const renderPortfolioRisk =
        (selectedModelPortfolioMetrics: IPortfolioMetrics, currentPlanPortfolioMetrics: IPortfolioMetrics) => {
        if (!selectedModelPortfolioMetrics.fivePctVar1y || !currentPlanPortfolioMetrics.fivePctVar1y) {
            return null;
        }
        const rowProps: IRowProps = {
            label: METRIC_PORTFOLIO_RISK,
            modelValue: calculatePortfolioRisk(selectedModelPortfolioMetrics),
            currentPlanValue: calculatePortfolioRisk(currentPlanPortfolioMetrics),
            tooltipText: [TOOLTIP_PORTFOLIO_RISK]
        };

        return renderRow(rowProps);
    };

    const calculatePortfolioUpside = (portfolioMetrics: IPortfolioMetrics): string => {
        return formatPercentBasedOnLocale((portfolioMetrics.fivePctUpside1y! - investmentAdvisoryFees / 100) * 100);
    };

    const renderPortfolioUpside =
        (selectedModelPortfolioMetrics: IPortfolioMetrics, currentPlanPortfolioMetrics: IPortfolioMetrics) => {
        if (!selectedModelPortfolioMetrics.fivePctUpside1y || !currentPlanPortfolioMetrics.fivePctUpside1y) {
            return null;
        }
        const rowProps: IRowProps = {
            label: METRIC_PORTFOLIO_UPSIDE,
            modelValue: calculatePortfolioUpside(selectedModelPortfolioMetrics),
            currentPlanValue: calculatePortfolioUpside(currentPlanPortfolioMetrics),
            tooltipText: [TOOLTIP_PORTFOLIO_UPSIDE]
        };

        return renderRow(rowProps);
    };

    const renderTable =
        (selectedModelPortfolioMetrics: IPortfolioMetrics, currentPlanPortfolioMetrics: IPortfolioMetrics) => {
        return <table className={"model-performance-metrics__table"}>
            <thead>
                <tr>
                    <th className={"model-performance-metrics__table-header"}/>
                    <th className={"model-performance-metrics__table-model-header"}
                        data-testid="model-performance-metrics__table-model-header">
                        Model Portfolio
                        <div>{props.selectedModelPortfolio.name}</div>
                    </th>
                    <th className={"model-performance-metrics__table-current-header"}
                        data-testid={"model-performance-metrics__table-current-header"}>
                        Your Portfolio
                    </th>
                </tr>
            </thead>
            <tbody>
                {renderNominalReturn(selectedModelPortfolioMetrics, currentPlanPortfolioMetrics)}
                {renderInvestmentAdvisoryFees()}
                {renderInvestmentNominalReturn(selectedModelPortfolioMetrics, currentPlanPortfolioMetrics)}
                {renderVolatility(selectedModelPortfolioMetrics, currentPlanPortfolioMetrics)}
                {renderRiskRewardRatio(selectedModelPortfolioMetrics, currentPlanPortfolioMetrics)}
                {renderPortfolioRisk(selectedModelPortfolioMetrics, currentPlanPortfolioMetrics)}
                {renderPortfolioUpside(selectedModelPortfolioMetrics, currentPlanPortfolioMetrics)}
            </tbody>
        </table>;
    };

    const renderDisclaimerText = () => {
        return <div className={"model-performance-metrics__disclaimer-text"} data-testid="model-performance-metrics__disclaimer-text">
            <b>* Hypothetical Performance and Characteristics:</b> The projections and other information generated by
            Aon regarding the likelihood of various investment outcomes are hypothetical in nature, do not reflect
            actual investment results, and are not predictive of future results. Economic factors and assumptions
            depend on current market conditions and, as such, may change over time. Forecasts, estimates and other
            information contained herein are based upon proprietary research and should not be considered as investment
            advice or a recommendation of any particular security, strategy or investment product. There is no
            guarantee that the results portrayed will be achieved. Please see the “Methodology and Disclosures” section
            for important information on the calculation as well as risks and limitations of using hypothetical
            performance in making investment decisions.
        </div>;
    };

    const getFilterMetricsBySelectedModelPortfolio = (): IPortfolioMetrics | undefined => {
        return props.performanceMetrics.modelPortfolioMetrics!
            .find(it => it.portfolioId === props.selectedModelPortfolio.id);
    };

    const renderComponent = () => {
        if (!props.performanceMetrics.currentPlanPortfolioMetrics
            || !props.performanceMetrics.modelPortfolioMetrics
            || props.performanceMetrics.modelPortfolioMetrics.length === 0) {
            return null;
        }

        const  currentPlanPortfolioMetrics: IPortfolioMetrics = props.performanceMetrics.currentPlanPortfolioMetrics;
        const selectedModelPortfolioMetrics = getFilterMetricsBySelectedModelPortfolio();
        if (!selectedModelPortfolioMetrics ||
            !hasNominalReturn(selectedModelPortfolioMetrics, currentPlanPortfolioMetrics)) {
            return null;
        }

        return <div>
            <div className="model-performance-metrics__container" data-testid="model-performance-metrics__container">
                <h2>Hypothetical Performance and Characteristics *</h2>
                {renderTable(selectedModelPortfolioMetrics, currentPlanPortfolioMetrics)}
            </div>
            {renderDisclaimerText()}
        </div>;
    };

    return renderComponent();
};